import React from 'react';
import {
  AlertThresholdKind,
  IAlertThreshold,
  IAlertThresholdCompareOp,
  IAlertThresholdCustom,
  IComparisonConfig,
  ILogicalOperator,
  LogicalOperatorKey,
} from '../../../utils/apollo/graphqlSchema';
import styled from 'styled-components';
import { FieldLabel, VerticalField } from '../../Form';
import CustomThresholdsPair from './CustomThresholdsPair';
import { anomalyDetectionInfoTooltipOption } from '../../KubitTooltip/globalTooltips';
import InformationalTooltip from '../../InformationalTooltip';
import { SingleSelectMenu } from '../../Dropdown';
import { freeze } from '@reduxjs/toolkit';
import { ISingleSelectMenuOption } from '../../Dropdown/SingleSelectMenu/types';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { buttonClicked } from '../../../utils/analytics/events/button/events';

interface IProps {
  allowCustom: boolean;
  comparisonConfig: IComparisonConfig | null;
  compareOps: IAlertThresholdCompareOp[];
  thresholds: (IAlertThreshold | null)[];
  setThresholds: (newThresholds: (IAlertThreshold | null)[]) => void;
}

const CriteriaWrapper = styled(VerticalField)`
  align-items: start;
  margin-bottom: 16px;
`;

const AnomalyTabWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const criteriaOptions = freeze<ISingleSelectMenuOption[]>(
  [
    {
      text: 'Anomaly detected',
      value: AlertThresholdKind.Anomaly,
    },
    {
      text: 'Custom threshold met',
      value: AlertThresholdKind.Custom,
    },
  ],
  true,
);

export default ({
  allowCustom,
  comparisonConfig,
  compareOps,
  thresholds,
  setThresholds,
}: IProps) => {
  const { track } = useAnalytics();

  const currentKind = thresholds[0]?.kind ?? AlertThresholdKind.Anomaly;
  const isCustom = currentKind === AlertThresholdKind.Custom;
  const selectAnomaly = () => {
    setThresholds([
      {
        __typename: 'AlertThresholdAnomaly',
        kind: AlertThresholdKind.Anomaly,
      },
    ]);
  };

  const defaultCustomThreshold = freeze<IAlertThresholdCustom>(
    {
      __typename: 'AlertThresholdCustom',
      kind: AlertThresholdKind.Custom,
      compareOp: compareOps[0],
      compareValue: 0,
      logicOp: null,
      valueIsPercent: false,
    },
    true,
  );

  const selectCustom = () => {
    if (isCustom) {
      return;
    }
    setThresholds([defaultCustomThreshold]);
  };

  // XXX we support max two thresholds, try to make this easier
  const firstCustomThreshold: IAlertThresholdCustom | null =
    isCustom && thresholds[0]?.__typename === 'AlertThresholdCustom'
      ? thresholds[0]
      : null;
  const secondCustomThreshold: IAlertThresholdCustom | null =
    isCustom && thresholds[1]?.__typename === 'AlertThresholdCustom'
      ? thresholds[1]
      : null;

  const logicOpKey = firstCustomThreshold
    ? firstCustomThreshold.logicOp?.key ?? null
    : null;

  const getLogicOp = (): ILogicalOperator | null =>
    logicOpKey
      ? { __typename: 'LogicalOperator', key: logicOpKey, name: logicOpKey }
      : null;

  const setLogicOpKey = (newKey: LogicalOperatorKey) => {
    const newThresholds = [...thresholds];
    const firstNewThreshold = newThresholds[0] ?? defaultCustomThreshold;
    if (firstNewThreshold.__typename === 'AlertThresholdCustom') {
      newThresholds[0] = {
        ...firstNewThreshold,
        logicOp: { __typename: 'LogicalOperator', key: newKey, name: newKey },
      };
    }
    if (newThresholds.length < 2) {
      newThresholds.push(defaultCustomThreshold);
    }
    setThresholds(newThresholds);
  };

  const setCustomThreshold = (
    ix: number,
    newThreshold: IAlertThresholdCustom,
  ) => {
    const newThresholds = [...thresholds];
    while (newThresholds.length - 1 < ix) {
      newThresholds.push({
        ...defaultCustomThreshold,
        logicOp: getLogicOp(),
      });
    }
    newThresholds[ix] = {
      ...newThreshold,
      logicOp: ix < newThresholds.length - 1 ? getLogicOp() : null, // last has null logic op
    };
    setThresholds(newThresholds);
  };

  const setFirstCustomThreshold = (newThreshold: IAlertThresholdCustom) =>
    setCustomThreshold(0, newThreshold);
  const setSecondCustomThreshold = (newThreshold: IAlertThresholdCustom) =>
    setCustomThreshold(1, newThreshold);

  const removeSecondCustomThreshold = () => {
    const remainingThreshold = thresholds[0]
      ? { ...thresholds[0], logicOp: null }
      : null;
    setThresholds([remainingThreshold]);
  };

  const handleChangeCriteria = (value: string) => {
    const anomalySelected = value === AlertThresholdKind.Anomaly;

    const trackAction = anomalySelected
      ? 'anomaly detected'
      : 'custom threshold met';

    track(buttonClicked({ context: 'schedule', action: trackAction }));

    if (anomalySelected) {
      selectAnomaly();
    } else {
      selectCustom();
    }
  };

  return (
    <VerticalField>
      {allowCustom && (
        <CriteriaWrapper>
          <FieldLabel>Criteria</FieldLabel>
          <SingleSelectMenu
            value={currentKind}
            options={criteriaOptions}
            onChange={handleChangeCriteria}
          />
        </CriteriaWrapper>
      )}
      {isCustom && firstCustomThreshold ? (
        <CustomThresholdsPair
          comparisonConfig={comparisonConfig}
          compareOps={compareOps}
          firstCustomThreshold={firstCustomThreshold}
          setFirstCustomThreshold={setFirstCustomThreshold}
          secondCustomThreshold={secondCustomThreshold}
          setSecondCustomThreshold={setSecondCustomThreshold}
          removeSecondCustomThreshold={removeSecondCustomThreshold}
          logicOpKey={logicOpKey}
          setLogicOpKey={setLogicOpKey}
        />
      ) : (
        <AnomalyTabWrapper>
          <FieldLabel>
            An email will be sent to the recipients when a report surges or
            drops outside of the expected range.{' '}
          </FieldLabel>
          <InformationalTooltip
            tooltipOption={anomalyDetectionInfoTooltipOption}
          />
        </AnomalyTabWrapper>
      )}
    </VerticalField>
  );
};
